import React, { useState } from 'react';
import {
  X, Palette, MessageSquare, MessageCircle, Phone, Eye, CheckCircle, MessagesSquare,
  Mic, Headphones, Bot, User, RefreshCw,
} from 'lucide-react';
import api from '../api';
import Toast from './Toast';
import { ComboLauncherIcon, resolveComboLauncherIconId } from './UnifiedEmbedWidget';

const LAUNCHER_ICON_OPTIONS = [
  { name: 'Dynamic', value: 'dynamic', Icon: RefreshCw, description: 'Follows active channel (chat or voice)' },
  { name: 'Combo', value: 'messages-square', Icon: MessagesSquare, description: 'Dual chat + voice symbol' },
  { name: 'Chat', value: 'message-square', Icon: MessageSquare },
  { name: 'Chat Circle', value: 'message-circle', Icon: MessageCircle },
  { name: 'Phone', value: 'phone', Icon: Phone },
  { name: 'Mic', value: 'mic', Icon: Mic },
  { name: 'Headphones', value: 'headphones', Icon: Headphones },
  { name: 'Bot', value: 'bot', Icon: Bot },
  { name: 'User', value: 'user', Icon: User },
];

const COLOR_PRESETS = [
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Green', value: '#10b981' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Dark', value: '#1e293b' },
];

const POSITION_OPTIONS = [
  { name: 'Bottom Right', value: 'bottom-right' },
  { name: 'Bottom Left', value: 'bottom-left' },
  { name: 'Top Right', value: 'top-right' },
  { name: 'Top Left', value: 'top-left' },
];

const ColorPicker = ({ label, fieldName, value, onChange, pickerId }) => (
  <div className="space-y-3">
    <label className="text-sm font-semibold text-foreground">{label}</label>
    <div className="grid grid-cols-5 gap-2">
      {COLOR_PRESETS.map((c) => (
        <button
          key={c.value}
          type="button"
          onClick={() => onChange(fieldName, c.value)}
          title={c.name}
          className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all ${
            value === c.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
          }`}
        >
          <div className="w-7 h-7 rounded-full" style={{ backgroundColor: c.value }} />
          <span className="text-[10px] text-foreground">{c.name}</span>
          {value === c.value && <CheckCircle className="w-3 h-3 text-primary" />}
        </button>
      ))}
    </div>
    <div className="bg-muted/30 border border-border/60 rounded-xl p-4 flex items-center gap-4">
      <label htmlFor={pickerId} className="relative cursor-pointer">
        <div className="w-16 h-10 rounded-lg border-2 border-white shadow-md" style={{ backgroundColor: value }} />
        <input
          id={pickerId}
          type="color"
          value={value}
          onChange={(e) => onChange(fieldName, e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />
      </label>
      <input
        type="text"
        value={value.replace('#', '')}
        onChange={(e) => {
          const v = '#' + e.target.value.replace('#', '');
          if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) onChange(fieldName, v);
        }}
        className="flex-1 px-3 py-2 text-sm font-mono bg-background border-2 border-border rounded-lg uppercase"
        maxLength={6}
      />
    </div>
  </div>
);

const Toggle = ({ checked, onChange, label }) => (
  <div className="flex items-center gap-3">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
        checked ? 'bg-primary' : 'bg-muted'
      }`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`} />
    </button>
    <span className="text-sm text-muted-foreground">{label}</span>
  </div>
);

const UnifiedWidgetThemeCustomizer = ({ agent, onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState('combo');
  const [settings, setSettings] = useState({
    combo_widget_default_channel: agent?.combo_widget_default_channel || 'chat',
    combo_widget_button_label: agent?.combo_widget_button_label || 'Contact us',
    combo_widget_title: agent?.combo_widget_title || agent?.name || 'Assistant',
    combo_widget_subtitle: agent?.combo_widget_subtitle || 'Chat or call — your choice',
    combo_widget_theme_mode: agent?.combo_widget_theme_mode || agent?.chat_theme_mode || 'light',
    combo_widget_primary_color: agent?.combo_widget_primary_color || agent?.chat_primary_color || '#6366f1',
    combo_widget_button_color: agent?.combo_widget_button_color || agent?.chat_button_color || '#6366f1',
    combo_widget_button_position: agent?.combo_widget_button_position || agent?.chat_button_position || 'bottom-right',
    combo_widget_pulse_animation: agent?.combo_widget_pulse_animation ?? true,
    combo_widget_show_label: agent?.combo_widget_show_label ?? true,
    combo_widget_show_channel_switch: agent?.combo_widget_show_channel_switch ?? true,
    combo_widget_launcher_icon: agent?.combo_widget_launcher_icon || 'dynamic',
    chat_theme_mode: agent?.chat_theme_mode || 'light',
    chat_primary_color: agent?.chat_primary_color || '#6366f1',
    chat_button_icon: agent?.chat_button_icon || 'message-square',
    chat_button_color: agent?.chat_button_color || '#6366f1',
    chat_greeting_enabled: agent?.chat_greeting_enabled || false,
    chat_greeting_message: agent?.chat_greeting_message || '',
    chat_greeting_dynamic: agent?.chat_greeting_dynamic || false,
    voice_widget_theme_mode: agent?.voice_widget_theme_mode || 'light',
    voice_widget_primary_color: agent?.voice_widget_primary_color || '#6366f1',
    voice_widget_button_color: agent?.voice_widget_button_color || '#6366f1',
    voice_widget_icon: agent?.voice_widget_icon || 'phone',
    voice_widget_title: agent?.voice_widget_title || agent?.name || 'Voice Assistant',
    voice_widget_subtitle: agent?.voice_widget_subtitle || 'Click to start talking',
    voice_widget_pulse_animation: agent?.voice_widget_pulse_animation ?? true,
    voice_widget_avatar_type: agent?.voice_widget_avatar_type || 'icon',
    voice_widget_avatar_color: agent?.voice_widget_avatar_color || '#6366f1',
    voice_widget_welcome_message: agent?.voice_widget_welcome_message || '',
    voice_widget_show_transcript: agent?.voice_widget_show_transcript ?? true,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const set = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));
  const toggle = (field) => setSettings(prev => ({ ...prev, [field]: !prev[field] }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.put(`/agents/${agent.id}`, settings);
      setToast({ message: 'Combo widget settings saved!', type: 'success' });
      setTimeout(() => {
        onSave?.(settings);
        onClose();
      }, 800);
    } catch {
      setToast({ message: 'Failed to save settings', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = () => {
    const params = new URLSearchParams({
      view: 'button',
      theme: settings.combo_widget_theme_mode,
      primary: settings.combo_widget_primary_color.replace('#', ''),
      btnColor: settings.combo_widget_button_color.replace('#', ''),
      position: settings.combo_widget_button_position,
      channel: settings.combo_widget_default_channel,
      launcherIcon: settings.combo_widget_launcher_icon,
    });
    window.open(`${window.location.origin}/embed/combo-widget/${agent.id}?${params}`, '_blank');
  };

  const tabs = [
    { id: 'combo', label: 'Combo Widget', Icon: MessagesSquare },
    { id: 'chat', label: 'Chat Panel', Icon: MessageSquare },
    { id: 'voice', label: 'Voice Panel', Icon: Phone },
  ];

  const previewSettings = {
    launcherIcon: settings.combo_widget_launcher_icon,
    defaultChannel: settings.combo_widget_default_channel,
  };
  const previewAgent = {
    chat_button_icon: settings.chat_button_icon,
    voice_widget_icon: settings.voice_widget_icon,
  };
  const previewIconChat = resolveComboLauncherIconId(previewSettings, previewAgent, 'chat');
  const previewIconVoice = resolveComboLauncherIconId(previewSettings, previewAgent, 'voice');

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-elegant">
        <div className="bg-gradient-secondary text-white p-5 flex justify-between items-center rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
              <Palette className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Customize Combo Widget</h2>
              <p className="text-white/70 text-xs mt-0.5">Shared launcher plus chat and voice panel styles</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/10">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex border-b border-border px-6 pt-4 gap-1">
          {tabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-medium transition-colors ${
                activeTab === id
                  ? 'bg-primary/10 text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {activeTab === 'combo' && (
            <>
              <div className="space-y-3">
                <label className="text-sm font-semibold text-foreground">Default Channel</label>
                <div className="grid grid-cols-2 gap-3">
                  {['chat', 'voice'].map((ch) => (
                    <button
                      key={ch}
                      type="button"
                      onClick={() => set('combo_widget_default_channel', ch)}
                      className={`px-4 py-3 rounded-lg border-2 capitalize transition-all ${
                        settings.combo_widget_default_channel === ch
                          ? 'border-primary bg-primary/10 text-primary font-semibold'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      {ch}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Widget Title</label>
                  <input
                    type="text"
                    value={settings.combo_widget_title}
                    onChange={(e) => set('combo_widget_title', e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Launcher Label</label>
                  <input
                    type="text"
                    value={settings.combo_widget_button_label}
                    onChange={(e) => set('combo_widget_button_label', e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground">Subtitle</label>
                <input
                  type="text"
                  value={settings.combo_widget_subtitle}
                  onChange={(e) => set('combo_widget_subtitle', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                />
              </div>

              <div className="space-y-3">
                <label className="text-sm font-semibold text-foreground">Launcher Icon</label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {LAUNCHER_ICON_OPTIONS.map(({ name, value, Icon, description }) => (
                    <button
                      key={value}
                      type="button"
                      title={description || name}
                      onClick={() => set('combo_widget_launcher_icon', value)}
                      className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                        settings.combo_widget_launcher_icon === value
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border hover:border-primary/50 text-foreground'
                      }`}
                    >
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                        style={{ backgroundColor: settings.combo_widget_button_color }}
                      >
                        {value === 'dynamic' ? (
                          <Icon className="w-5 h-5" />
                        ) : (
                          <ComboLauncherIcon iconId={value} className="w-5 h-5" />
                        )}
                      </div>
                      <span className="text-[11px] font-medium text-center leading-tight">{name}</span>
                      {settings.combo_widget_launcher_icon === value && (
                        <CheckCircle className="w-3.5 h-3.5 text-primary" />
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Dynamic switches between chat and voice icons based on the default channel (and active channel when open).
                  Static icons always show the same symbol.
                </p>
                {settings.combo_widget_launcher_icon === 'dynamic' && (
                  <div className="flex items-center gap-4 p-3 bg-muted/30 rounded-xl border border-border">
                    <span className="text-xs text-muted-foreground">Preview:</span>
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col items-center gap-1">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-white"
                          style={{ backgroundColor: settings.combo_widget_button_color }}
                        >
                          <ComboLauncherIcon iconId={previewIconChat} className="w-4 h-4" />
                        </div>
                        <span className="text-[10px] text-muted-foreground">Chat</span>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-white"
                          style={{ backgroundColor: settings.combo_widget_button_color }}
                        >
                          <ComboLauncherIcon iconId={previewIconVoice} className="w-4 h-4" />
                        </div>
                        <span className="text-[10px] text-muted-foreground">Voice</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <label className="text-sm font-semibold text-foreground">Theme Mode</label>
                <div className="grid grid-cols-3 gap-3">
                  {['light', 'dark', 'auto'].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => set('combo_widget_theme_mode', mode)}
                      className={`px-4 py-3 rounded-lg border-2 capitalize ${
                        settings.combo_widget_theme_mode === mode
                          ? 'border-primary bg-primary/10 text-primary font-semibold'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Applies to the combo shell, chat panel, and voice panel. Auto matches the visitor&apos;s system preference.
                </p>
              </div>

              <ColorPicker
                label="Header / Accent Color"
                fieldName="combo_widget_primary_color"
                value={settings.combo_widget_primary_color}
                onChange={set}
                pickerId="combo-primary"
              />
              <ColorPicker
                label="Launcher Button Color"
                fieldName="combo_widget_button_color"
                value={settings.combo_widget_button_color}
                onChange={set}
                pickerId="combo-btn"
              />

              <div className="space-y-3">
                <label className="text-sm font-semibold text-foreground">Button Position</label>
                <div className="grid grid-cols-2 gap-3">
                  {POSITION_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => set('combo_widget_button_position', opt.value)}
                      className={`px-4 py-3 rounded-lg border-2 text-sm ${
                        settings.combo_widget_button_position === opt.value
                          ? 'border-primary bg-primary/10 text-primary font-semibold'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      {opt.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4 bg-muted/20 rounded-xl p-4 border border-border">
                <Toggle
                  checked={settings.combo_widget_show_label}
                  onChange={() => toggle('combo_widget_show_label')}
                  label="Show launcher text label"
                />
                <Toggle
                  checked={settings.combo_widget_pulse_animation}
                  onChange={() => toggle('combo_widget_pulse_animation')}
                  label="Pulse animation on launcher button"
                />
                <Toggle
                  checked={settings.combo_widget_show_channel_switch}
                  onChange={() => toggle('combo_widget_show_channel_switch')}
                  label="Show chat/voice flip toggle in header"
                />
              </div>
            </>
          )}

          {activeTab === 'chat' && (
            <>
              <ColorPicker
                label="Chat Primary Color"
                fieldName="chat_primary_color"
                value={settings.chat_primary_color}
                onChange={set}
                pickerId="chat-primary"
              />
              <div className="space-y-3">
                <label className="text-sm font-semibold text-foreground">Chat Theme Mode</label>
                <div className="grid grid-cols-3 gap-3">
                  {['light', 'dark', 'auto'].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => set('chat_theme_mode', mode)}
                      className={`px-4 py-3 rounded-lg border-2 capitalize ${
                        settings.chat_theme_mode === mode
                          ? 'border-primary bg-primary/10 text-primary font-semibold'
                          : 'border-border'
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>
              <Toggle
                checked={settings.chat_greeting_enabled}
                onChange={() => toggle('chat_greeting_enabled')}
                label="Enable greeting when chat opens"
              />
              {settings.chat_greeting_enabled && (
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Greeting Message</label>
                  <textarea
                    value={settings.chat_greeting_message}
                    onChange={(e) => set('chat_greeting_message', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm resize-none"
                  />
                  <Toggle
                    checked={settings.chat_greeting_dynamic}
                    onChange={() => toggle('chat_greeting_dynamic')}
                    label="Generate greeting dynamically via LLM"
                  />
                </div>
              )}
            </>
          )}

          {activeTab === 'voice' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Voice Panel Title</label>
                  <input
                    type="text"
                    value={settings.voice_widget_title}
                    onChange={(e) => set('voice_widget_title', e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Voice Panel Subtitle</label>
                  <input
                    type="text"
                    value={settings.voice_widget_subtitle}
                    onChange={(e) => set('voice_widget_subtitle', e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                  />
                </div>
              </div>
              <ColorPicker
                label="Voice Accent Color"
                fieldName="voice_widget_primary_color"
                value={settings.voice_widget_primary_color}
                onChange={set}
                pickerId="voice-primary"
              />
              <ColorPicker
                label="Voice Avatar Color"
                fieldName="voice_widget_avatar_color"
                value={settings.voice_widget_avatar_color}
                onChange={set}
                pickerId="voice-avatar"
              />
              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground">Welcome Message (before call)</label>
                <textarea
                  value={settings.voice_widget_welcome_message}
                  onChange={(e) => set('voice_widget_welcome_message', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm resize-none"
                  placeholder="Optional message shown before starting a call"
                />
              </div>
              <Toggle
                checked={settings.voice_widget_show_transcript}
                onChange={() => toggle('voice_widget_show_transcript')}
                label="Show live transcript during voice calls"
              />
            </>
          )}
        </div>

        <div className="border-t border-border p-4 bg-muted/30 flex justify-between items-center">
          <button
            type="button"
            onClick={handlePreview}
            className="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-sm flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg text-sm disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default UnifiedWidgetThemeCustomizer;
